import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { ChartLine, ClipboardText, FileText, ListChecks, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">Welcome back, {user?.name}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-card-inspections" className="bg-white border border-gray-200 p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">TOTAL INSPECTIONS</p>
              <p className="mt-2 text-3xl font-bold text-gray-900 mono">{stats?.total_inspections || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-sm">
              <ListChecks size={24} weight="duotone" className="text-blue-600" />
            </div>
          </div>
        </div>

        <div data-testid="stat-card-pass-rate" className="bg-white border border-gray-200 p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">PASS RATE</p>
              <p className="mt-2 text-3xl font-bold text-green-600 mono">{stats?.pass_rate || 0}%</p>
            </div>
            <div className="bg-green-100 p-3 rounded-sm">
              <CheckCircle size={24} weight="duotone" className="text-green-600" />
            </div>
          </div>
        </div>

        <div data-testid="stat-card-defects" className="bg-white border border-gray-200 p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">OPEN DEFECTS</p>
              <p className="mt-2 text-3xl font-bold text-red-600 mono">{stats?.open_defects || 0}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-sm">
              <Warning size={24} weight="duotone" className="text-red-600" />
            </div>
          </div>
        </div>

        <div data-testid="stat-card-ppap" className="bg-white border border-gray-200 p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">PENDING PPAP</p>
              <p className="mt-2 text-3xl font-bold text-blue-600 mono">{stats?.pending_ppap || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-sm">
              <ClipboardText size={24} weight="duotone" className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="border-b border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900">Quick Stats</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Passed Inspections</span>
              <span className="text-sm font-semibold text-green-600 mono">{stats?.passed_inspections || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Failed Inspections</span>
              <span className="text-sm font-semibold text-red-600 mono">{stats?.failed_inspections || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Active APQP Projects</span>
              <span className="text-sm font-semibold text-gray-900 mono">{stats?.active_apqp || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="border-b border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900">System Status</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Database</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-sm">OPERATIONAL</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Storage</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-sm">OPERATIONAL</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Last Updated</span>
              <span className="text-sm font-medium text-gray-900 mono">{new Date().toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
